/**
 * DFD(Data Flow Diagram) 표기 규칙 검사.
 *   - 워크플로의 시작과 끝은 데이터(SAM파일/DB테이블/내부데이터)
 *   - 데이터와 데이터 사이에는 처리(데이터처리/Filter/Join)가, 처리와 처리 사이에는 데이터가 있어야 한다
 *   - 제어 노드(분기/합류/사용자확인)는 데이터를 바꾸지 않는 "투명한 통과점" — 규칙 검사에서 건너뛴다(예외)
 *   - 실행 노드(실행/외부명령어/SQL실행)는 데이터 흐름 밖의 특수 노드 — 검사에서 제외(연결 엣지도 데이터 흐름으로 보지 않음)
 *   - 그룹(서브플로/반복그룹)은 경계일 뿐이고, 메모는 무시
 *   - **순환(흐름이 되돌아오는 것)은 어떤 노드·엣지든 위반**이다 — 실행 순서를 정할 수 없다. 예외 허용도 안 된다
 * 실무 예외는 엣지 data.dfdException / 노드 data.dfdException 으로 표시해 검사에서 제외한다(특수한 경우로 다룬다).
 */
import type { Node, Edge } from '@xyflow/react';
import i18n from '../../../i18n';
import { isErrorEdge } from './errorFlow';

export type DfdClass = 'data' | 'process' | 'control' | 'exec' | 'ignore';

export function dfdClass(type?: string): DfdClass {
  switch (type) {
    case 'csvData': case 'rdbData': case 'innerData': return 'data';
    case 'transformLoad': case 'filter': case 'join': case 'aggregate': case 'sort': case 'lookup': case 'quality': case 'compare': return 'process';
    case 'branch': case 'merge': case 'userConfirm': return 'control';
    case 'runWorkflow': case 'shellCommand': case 'sqlScript': return 'exec'; // 실행 노드: 데이터 흐름 밖의 특수 노드 — 검사 제외
    default: return 'ignore'; // group/subflow/repeatGroup/memo/placeholder
  }
}

export interface DfdViolation {
  id: string;                 // 고유 키 (엣지 id 또는 node:<id>:<start|end>)
  kind: 'edge' | 'start' | 'end' | 'cycle';
  edgeIds: string[];          // 관련 엣지 (제어 노드를 거친 경우 여러 개)
  nodeIds: string[];          // 관련 노드
  message: string;
}

const label = (n?: Node) => String((n?.data as any)?.label || n?.type || '?');

/**
 * 제어 노드를 투명하게 통과해 "실질적인" 이웃(데이터/처리)을 찾는다.
 * 반환: [도달 노드, 거쳐 온 엣지 id 목록]
 */
function effectiveNeighbors(start: Node, dir: 'out' | 'in', byId: Map<string, Node>, adj: Map<string, Edge[]>): Array<[Node, string[]]> {
  const result: Array<[Node, string[]]> = [];
  const seen = new Set<string>();
  const stack: Array<[string, string[]]> = [[start.id, []]];
  while (stack.length) {
    const [id, path] = stack.pop()!;
    for (const e of adj.get(id) || []) {
      const nextId = dir === 'out' ? e.target : e.source;
      const n = byId.get(nextId);
      if (!n || seen.has(nextId + ':' + e.id)) continue;
      seen.add(nextId + ':' + e.id);
      const cls = dfdClass(n.type);
      if (cls === 'exec') continue; // 실행 노드로 가는/오는 엣지는 데이터 흐름으로 보지 않는다
      if (cls === 'control' || cls === 'ignore') stack.push([nextId, [...path, e.id]]);
      else result.push([n, [...path, e.id]]);
    }
  }
  return result;
}

export function checkDfd(nodes: Node[], edges: Edge[]): DfdViolation[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const outAdj = new Map<string, Edge[]>();
  const inAdj = new Map<string, Edge[]>();
  for (const e of edges) {
    if (!byId.has(e.source) || !byId.has(e.target)) continue;
    outAdj.set(e.source, [...(outAdj.get(e.source) || []), e]);
    inAdj.set(e.target, [...(inAdj.get(e.target) || []), e]);
  }
  // 예외 흐름(데이터처리 실패 시 가는 길)은 값이 흐르는 길이 아니라 데이터↔처리 교대를 따지지 않는다 (utils/errorFlow)
  const excepted = (e: Edge) => !!(e.data as any)?.dfdException || isErrorEdge(e);
  const nodeExcepted = (n: Node) => !!(n.data as any)?.dfdException;
  const violations: DfdViolation[] = [];
  const seenEdgePairs = new Set<string>();

  for (const n of nodes) {
    const cls = dfdClass(n.type);
    if (cls === 'ignore' || cls === 'control' || cls === 'exec') continue;
    const outs = effectiveNeighbors(n, 'out', byId, outAdj);
    const ins = effectiveNeighbors(n, 'in', byId, inAdj);

    // 데이터→데이터 / 처리→처리
    for (const [m, path] of outs) {
      const key = `${n.id}>${m.id}`;
      if (seenEdgePairs.has(key)) continue;
      seenEdgePairs.add(key);
      if (path.some((eid) => excepted(edges.find((e) => e.id === eid)!))) continue;
      const mcls = dfdClass(m.type);
      if (cls === mcls) {
        violations.push({
          id: `edge:${path.join('+')}`, kind: 'edge', edgeIds: path, nodeIds: [n.id, m.id],
          message: cls === 'process'
            ? i18n.t('workflow:dfd.processToProcess', { from: label(n), to: label(m) })
            : i18n.t('workflow:dfd.dataToData', { from: label(n), to: label(m) }),
        });
      }
    }
    // 시작/끝은 데이터
    if (cls === 'process' && !nodeExcepted(n)) {
      const rawIn = (inAdj.get(n.id) || []).length;
      const rawOut = (outAdj.get(n.id) || []).length;
      if (rawIn === 0) {
        violations.push({ id: `node:${n.id}:start`, kind: 'start', edgeIds: [], nodeIds: [n.id],
          message: i18n.t('workflow:dfd.startsWithProcess', { node: label(n) }) });
      }
      if (rawOut === 0) {
        violations.push({ id: `node:${n.id}:end`, kind: 'end', edgeIds: [], nodeIds: [n.id],
          message: i18n.t('workflow:dfd.endsWithProcess', { node: label(n) }) });
      }
    }
  }
  violations.push(...findCycles(nodes, edges));
  return violations;
}

/**
 * 순환 찾기 — 모든 엣지(실행·제어·예외 흐름 포함)를 본다. 엣지 하나라도 되돌아오면 DAG 가 아니라 실행할 수 없다.
 * 순환마다 한 건: 되돌아오는 엣지에서 시작해 그 순환을 이루는 엣지들.
 */
export function findCycles(nodes: Node[], edges: Edge[]): DfdViolation[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const out = new Map<string, Edge[]>();
  edges.forEach((e) => { if (byId.has(e.source) && byId.has(e.target)) out.set(e.source, [...(out.get(e.source) || []), e]); });
  const state = new Map<string, 1 | 2>();          // 1 = 방문 중(스택 위), 2 = 끝
  const stack: Edge[] = [];                          // 지금 경로의 엣지
  const found: DfdViolation[] = [];
  const seenCycles = new Set<string>();

  const visit = (id: string) => {
    state.set(id, 1);
    for (const e of out.get(id) || []) {
      const s = state.get(e.target);
      if (s === 1) {
        // e.target 에서 시작해 지금까지의 경로 + e 가 순환
        const startIdx = stack.findIndex((x) => x.source === e.target);
        const cyc = [...(startIdx >= 0 ? stack.slice(startIdx) : []), e];
        const key = cyc.map((x) => x.id).sort().join(',');
        if (!seenCycles.has(key)) {
          seenCycles.add(key);
          const ids = cyc.map((x) => x.source);
          found.push({
            id: `cycle:${key}`, kind: 'cycle', edgeIds: cyc.map((x) => x.id), nodeIds: ids,
            message: i18n.t('workflow:dfd.cycle', { path: [...ids, e.target].map((nid) => label(byId.get(nid))).join(' → ') }),
          });
        }
      } else if (!s) {
        stack.push(e);
        visit(e.target);
        stack.pop();
      }
    }
    state.set(id, 2);
  };
  nodes.forEach((n) => { if (!state.has(n.id)) visit(n.id); });
  return found;
}

/**
 * **처리 노드와 닿지 않는 내부데이터**를 걷어내고 그 앞뒤를 바로 잇는다.
 *
 * 내부데이터는 "처리와 처리 사이를 흐르는 데이터" 를 그리기 위한 노드다(DFD 규칙이 그것을 요구한다).
 * 그런데 실행·제어 노드 사이에 끼워 넣으면 흐르는 데이터가 없어 **이름표만 남은 노드**가 된다 —
 * 마법사가 만든 그림과 샘플에 그런 노드가 있어 무엇을 뜻하는지 읽히지 않았다(현장 2026-09-22).
 * 실행·제어 노드는 앞뒤에 데이터 노드를 두지 않아도 되므로(dfdClass 가 검사에서 빼 둔다) 그냥 없앤다.
 */
export function pruneDecorativeData<N extends Node, E extends Edge>(nodes: N[], edges: E[]): { nodes: N[]; edges: E[] } {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const drop = new Set<string>();
  for (const n of nodes) {
    if (n.type !== 'innerData' || (n.data as any)?.dfdException) continue;
    const ins = edges.filter((e) => e.target === n.id);
    const outs = edges.filter((e) => e.source === n.id);
    const neighbours = [...ins.map((e) => e.source), ...outs.map((e) => e.target)];
    if (neighbours.some((id) => dfdClass(byId.get(id)?.type) === 'process')) continue;   // 진짜 데이터다 — 둔다
    drop.add(n.id);
  }
  if (drop.size === 0) return { nodes, edges };

  const kept = edges.filter((e) => !drop.has(e.source) && !drop.has(e.target));
  const bridged: E[] = [];
  for (const id of Array.from(drop)) {
    for (const i of edges.filter((e) => e.target === id && !drop.has(e.source))) {
      for (const o of edges.filter((e) => e.source === id && !drop.has(e.target))) {
        const already = [...kept, ...bridged].some((e) => e.source === i.source && e.target === o.target);
        if (!already) bridged.push({ ...i, id: `${i.source}-${o.target}`, source: i.source, target: o.target } as E);
      }
    }
  }
  return { nodes: nodes.filter((n) => !drop.has(n.id)), edges: [...kept, ...bridged] };
}
